import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot } from '@angular/router';

import { FixturePricingState } from '@cds/betting-offer/add-ons';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { MeetingModel, RacingUrlParam, WinGameFixturePreview } from '@frontend/sports/racing/feature/models';
import { TabsGroupSizesType } from '@frontend/ui/tabsgroup';
import { DeviceService, IntlService } from '@frontend/vanilla/core';

import { CrmPromotionProviderService, PromotionMap } from '../../../crm-promotion/crm-promotion-provider.service';
import { convertDayOfRaceToUrlParam } from '../../../horse-racing-shared/helpers';
import { AdaptiveLayoutState } from '../../../layout/adaptive-layout.service';
import { SubNavigationResolve } from '../../../sub-navigation/sub-navigation-resolve.service';
import { SubNavigationItem } from '../../../sub-navigation/sub-navigation.models';
import { GreyhoundSubNavigationService } from '../greyhounds/base/greyhounds-sub-navigation.service';

@Injectable()
export class HorsesRaceSubNavigationService extends SubNavigationResolve<MeetingModel> {
    private racingTabs: SubNavigationItem[];
    private promotedFixtures: StringDictionary<string>;
    private crmPromotedCustomClass = 'crm-race-item';
    sportId: number;

    constructor(
        private sitecore: Sitecore,
        private intlService: IntlService,
        private urlHelper: UrlHelperService,
        private crmPromotionProvider: CrmPromotionProviderService,
        private deviceService: DeviceService,
        routerEvents: RouterEventsService,
        private service: GreyhoundSubNavigationService,
    ) {
        super();

        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed()).subscribe((ids) => this.promoIdsChanged(ids));
        routerEvents.currentActivationEnd.pipe(takeUntilDestroyed()).subscribe((newRoute) => {
            const sport = newRoute?.snapshot.data['sport'];
            this.sportId = !!sport && sport === SportConstant.Greyhounds ? SportConstant.Greyhounds : SportConstant.Horses;
        });
    }

    buildSubNavigation(layout: AdaptiveLayoutState, routeSnapshot: ActivatedRouteSnapshot, model: MeetingModel): void {
        if (!model || !model.fixtures?.length) {
            this.tabs$.next([]);
            this.size$.next('' as TabsGroupSizesType);

            return;
        }

        if (!this.deviceService.isMobile && this.sportId === SportConstant.Greyhounds) {
            const routeTag = this.SetRouteTag(routeSnapshot, model);
            this.service.getDefaultTabsGreyhounds(routeTag).subscribe((routes) => {
                this.racingTabs = routes;
                this.setAttributes();
            });
        } else {
            const raceId = (routeSnapshot.params.raceId && routeSnapshot.params.raceId.toString()) || '';
            const raceTabs = model.fixtures.map((f) => this.fixtureToTab(f, raceId, model));
            const allTab = this.buildAllTab(raceId, model);

            raceTabs.unshift(allTab);
            this.racingTabs = raceTabs;
            this.setAttributes();
        }
    }

    private SetRouteTag(routeSnapshot: ActivatedRouteSnapshot, model: MeetingModel): any {
        const tagByModel = model.day.toLocaleLowerCase() === RacingUrlParam.Today.toString() ? RouteTag.RacingToday : RouteTag.RacingTomorrow;
        if (routeSnapshot.data && routeSnapshot.data.tag) {
            const tag = routeSnapshot.data.tag;

            return tag === model.day.toLocaleLowerCase() ? tag : tagByModel;
        }

        return tagByModel;
    }

    private setAttributes(): void {
        if (this.promotedFixtures && this.racingTabs) {
            this.racingTabs.forEach((rt) => {
                if (this.promotedFixtures[rt.compoundId!]) {
                    rt.badges = [
                        {
                            class: 'promotion-simple-badge badge badge-info',
                            icon: 'icon theme-badge',
                        },
                    ];
                    rt.classes = this.crmPromotedCustomClass;
                }
            });
        }

        if (this.racingTabs) {
            this.tabs$.next(this.racingTabs);
            this.size$.next('small');
        }
    }

    private promoIdsChanged(promoMap: PromotionMap): void {
        this.promotedFixtures = promoMap.fixtures;
        this.setAttributes();
    }

    private fixtureToTab(fixture: WinGameFixturePreview, raceId: string, model: MeetingModel): SubNavigationItem {
        const resulted = fixture.addons.isResulted || fixture.addons.pricingState === FixturePricingState.Resulted;

        return {
            id: +fixture.fixtureId,
            compoundId: fixture.id,
            title: this.intlService.formatDate(fixture.startDate, 'HH:mm') || '',
            url: this.urlHelper.getRacingUrlByFixture(fixture.sport.id, fixture),
            active: fixture.id === raceId,
            icon: resulted ? 'sports-race-resulted' : '',
            classes: [resulted ? 'race-resulted' : '', 'horse-racing-sub-navigation-tab-item'].join(' '),
            ignoreBrowserHistory: true,
        };
    }

    private buildAllTab(raceId: string, model: MeetingModel): SubNavigationItem {
        return {
            id: 0,
            title: this.sitecore.horseRacing.common.All,

            url: this.urlHelper.getRaceUrl(this.sportId, model.meeting, convertDayOfRaceToUrlParam(model.day)),
            active: !raceId,
            ignoreBrowserHistory: true,
        };
    }
}
